import React, { useEffect, useRef, useState } from "react";
import "./resellerStudio.css";
import { whatsappDigits } from "./catalogueShareCountries";
import { copyText } from "./copyText";
import type { CatalogueViewerGate } from "../../types/catalogue.types";

type Props = {
  url: string;
  recipientName: string;
  phone: string;
  countryIso: string;
  catalogueName?: string;
  emailed?: boolean;
  /** Open link: anyone with it can browse, no code. */
  open?: boolean;
  /** Open link that asks each viewer who they are first (NAME, PHONE or OTP). */
  gate?: CatalogueViewerGate;
  onDismiss: () => void;
};

/**
 * Shown right under "Create Share Link" in Settings, so the new link is there to copy or send
 * instead of only in the registry table further down.
 */
const ShareLinkReady: React.FC<Props> = ({
  url,
  recipientName,
  phone,
  countryIso,
  catalogueName,
  emailed,
  open,
  gate,
  onDismiss,
}) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    if (!(await copyText(url))) {
      // Clipboard blocked: select the link so the user can copy it by hand.
      inputRef.current?.focus();
      inputRef.current?.select();
      return;
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1800);
  };

  const message =
    `Hi ${recipientName.trim() || ""}, sharing our protected HKP catalogue` +
    (catalogueName ? `: "${catalogueName}"` : "") +
    (open ? `. Tap to open: ${url}` : `. Open it and verify with this mobile number: ${url}`);
  const wa = phone ? `https://wa.me/${whatsappDigits(countryIso, phone)}?text=${encodeURIComponent(message)}` : "";

  return (
    <div className="hkp-link-ready" role="status">
      <div className="hkp-link-ready-head">
        <strong>Link ready for {recipientName.trim() || "the recipient"}</strong>
        <button type="button" className="hkp-link-ready-close" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
      <p className="hkp-link-ready-note">
        {emailed ? "It was also emailed. " : ""}
        {open
          ? gate === "OTP"
            ? "Anyone with this link can open it after giving their name and mobile, verified by SMS code."
            : gate === "PHONE"
            ? "Anyone with this link can open it after giving their name and mobile."
            : gate === "NAME"
            ? "Anyone with this link can open it after giving their name."
            : "Anyone with this link can open it, no code needed."
          : "It opens only with their number."}
      </p>
      <div className="hkp-link-ready-row">
        <input
          ref={inputRef}
          className="hkp-link-ready-url"
          value={url}
          readOnly
          aria-label="Share link"
          onFocus={(e) => e.currentTarget.select()}
        />
        <button type="button" className="hkp-link-ready-btn" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy link"}
        </button>
        {wa ? (
          <a className="hkp-link-ready-btn is-wa" href={wa} target="_blank" rel="noopener noreferrer">
            WhatsApp
          </a>
        ) : null}
      </div>
    </div>
  );
};

export default ShareLinkReady;
